import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import { Stack, Typography, Divider, Drawer, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect } from 'react';
import { getTicketSummary } from '../../../../../api/tickets';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import TicketUsers from './ticketInfo/TicketUsers';
import TicketInformation from './ticketInfo/TicketInformation';
import Subtickets from './ticketInfo/Subtickets';
import { useSelector } from '../../../../../redux/store';
import TicketActions from './ticketInfo/TicketActions';
import TicketCategory from '../TicketCategory';
import EditTicketSubject from './ticketInfo/EditTicketSubject';
import MobileDrawerIndicator from './MobileDrawerIndicator';
import AttachOrderToTicket from '../AttachOrderToTicket';
import TicketSharedContent from '../TicketSharedContent';

MobileTicketInfo.propTypes = {
  facility: PropTypes.object,
  possibleUsers: PropTypes.array,
  ticketUsers: PropTypes.array,
  setIsTicketInfoOpen: PropTypes.func,
  setNewUsers: PropTypes.func,
  isAddingSubticket: PropTypes.bool,
  setIsAddingSubticket: PropTypes.func,
  ticketSubject: PropTypes.string,
  isTicketInfoOpen: PropTypes.bool,
  setExternalTicketSubject: PropTypes.func,
};

export default function MobileTicketInfo({
  facility,
  possibleUsers,
  ticketUsers,
  setIsTicketInfoOpen,
  isTicketInfoOpen,
  setNewUsers,
  isAddingSubticket,
  setIsAddingSubticket,
  ticketSubject,
  setExternalTicketSubject,
}) {
  const { id = '' } = useParams();
  const { mainTicket } = useSelector((state) => state.tickets.data.ticketInfoById[id] || {});
  const { ticketNumber } = useSelector((state) => state.tickets.data.ticketById[id] || {});
  const { data: { ticketTypes }, isLoading } = useSelector((state) => state.tickets || {});

  // Ticket summary state
  const [ticketSummary, setTicketSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  // If a summary is already saved for this ticket, use it
  useEffect(() => {
    if (mainTicket?.summary) {
      const summaryArray = mainTicket.summary.split('\n').filter(line => line.trim());
      setTicketSummary({ summary: summaryArray });
    } else {
      setTicketSummary(null);
    }
  }, [mainTicket?.summary, id]);

  const getTicketSummaryData = async () => {
    setIsLoadingSummary(true);
    try {
      const result = await getTicketSummary({ ticketId: parseInt(id, 10) });
      setTicketSummary(result.data);
    } catch (err) {
      setTicketSummary(null);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setIsTicketInfoOpen(true);
    },
    trackMouse: true,
  });


  return (
    <div
      {...handlers}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '40px',
        height: '100vh',
        zIndex: 2,
        background: 'transparent',
      }}
    >
      <Drawer
        anchor="right"
        open={isTicketInfoOpen}
        onClose={() => setIsTicketInfoOpen(false)}
        ModalProps={{
          BackdropProps: { invisible: true },
        }}
        PaperProps={{
          sx: { width: '75%' },
        }}
      >
        <Stack sx={{ p: 3 }}>
          <EditTicketSubject
            ticketSubject={ticketSubject}
            setExternalTicketSubject={setExternalTicketSubject}
            ticketNumber={ticketNumber}
          />
          <AttachOrderToTicket/>
          <Typography color="info.main" variant="h6">
            {facility?.shortName}
          </Typography>
          <TicketInformation facility={facility} />
          <Divider sx={{ my: 1.5, borderColor: 'text.secondary' }} />
          <TicketUsers possibleUsers={possibleUsers} ticketUsers={ticketUsers} setNewUsers={setNewUsers} />
          <Divider sx={{ my: 1.5, borderColor: 'text.secondary' }} />
          <TicketSharedContent fullWidth />
          {mainTicket && (
            <>
              {!mainTicket.locked && (
                <>
                  <Divider sx={{ my: 1.5, borderColor: 'text.secondary' }} />
                  <Subtickets
                    possibleUsers={possibleUsers}
                    setIsTicketInfoOpen={setIsTicketInfoOpen}
                    isAddingSubticket={isAddingSubticket}
                    setIsAddingSubticket={setIsAddingSubticket}
                  />
                </>
              )}
              <Divider sx={{ my: 1.5, borderColor: 'text.secondary' }} />
              <Typography sx={{mt: 1, fontSize: '14px'}}>
                Ticket Type: {isLoading ? 'Loading...' : ticketTypes?.find((type) => type.id === mainTicket?.ticketTypeId)?.name || 'Other'}
              </Typography>
              <TicketCategory
                ticketId={id}
                selectedTicketCategory={mainTicket.ticketCategoryId}
                setSelectedTicketCategory={() => {}}
              />
              <Divider sx={{ my: 1.5, borderColor: 'text.secondary' }} />
              <TicketActions />
              <Divider sx={{ my: 1.5, borderColor: 'text.secondary' }} />
              <Accordion disableGutters sx={{ '&:before': { display: 'none' }}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                  <Typography variant="h7" color="info.main" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src="/assets/bobAI/bobAIlogo_2.png" alt="bobAI Icon" style={{ width: 20, height: 20 }} />
                    Ticket Summary
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  {isLoadingSummary ? (
                    <CircularProgress size={20} sx={{ my: 1 }} />
                  ) : ticketSummary?.summary?.length === 1 ? (
                    <>
                      <Typography sx={{ fontSize: 14, mt: 0.5 }}>{ticketSummary.summary[0]}</Typography>
                      <Typography sx={{ fontSize: 8, color: '#888', mt: 0.5 }}>
                        *Summary generated by BobAI based on ticket messages and may not capture all details.
                      </Typography>
                    </>
                  ) : ticketSummary?.summary?.length > 0 ? (
                    <ul style={{ marginLeft: 18, marginTop: 4, marginBottom: 0, paddingLeft: 0 }}>
                      {ticketSummary.summary.map((item, index) => (
                        <li key={index} style={{ fontSize: 14, marginBottom: 4, color: 'inherit' }}>{item}</li>
                      ))}
                      <li style={{ fontSize: 8, color: '#888', listStyle: 'none', marginTop: 4 }}>
                        *Summary generated by BobAI based on ticket messages and may not capture all details.
                      </li>
                    </ul>
                  ) : (
                    <AwaitButton
                      fullWidth
                      variant="outlined"
                      size="small"
                      color="info"
                      onClick={getTicketSummaryData}
                      sx={{ whiteSpace: 'nowrap', mt: 1 }}
                    >
                      Generate Summary
                    </AwaitButton>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Stack>
      </Drawer>
      {!isTicketInfoOpen && <MobileDrawerIndicator setIsTicketInfoOpen={setIsTicketInfoOpen} />}
    </div>
  );
}
